import { useState, useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import type { Club, ClubPublicData, ClubPaymentSettingsFull } from "@/api/clubsApi";
import {
  useCreateClubMemberMutation,
  useRequestToJoinClubMutation,
  clubsApi,
} from "@/api/clubsApi";
import { useAppDispatch } from "@/store";
import { useLoginModal } from "@/components/auth/LoginModalProvider";
import type { User } from "@supabase/supabase-js";

interface JoinPanelProps {
  club: Club | ClubPublicData;
  clubId?: string;
  identifier: string;
  user: User | null;
  paymentSettings?: ClubPaymentSettingsFull | null;
  isPrivate?: boolean;
  isAcceptingMembers?: boolean;
  hasPendingRequest?: boolean;
}

export function JoinPanel({
  club,
  clubId,
  identifier,
  user,
  paymentSettings,
  isPrivate,
  isAcceptingMembers = true,
  hasPendingRequest,
}: JoinPanelProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { openLoginModal } = useLoginModal();
  const [createClubMember, { isLoading: joinLoading }] = useCreateClubMemberMutation();
  const [requestToJoinClub, { isLoading: requestLoading }] = useRequestToJoinClubMutation();
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  const isLoading = joinLoading || requestLoading;
  const isPaid = !!paymentSettings?.membership_enabled;
  const pendingJoinRef = useRef(false);

  // After login completes (via modal or OAuth redirect), execute the pending join
  useEffect(() => {
    if (user && clubId) {
      // Check ref (modal flow where page didn't reload)
      if (pendingJoinRef.current) {
        pendingJoinRef.current = false;
        handleJoin();
        return;
      }
      // Check sessionStorage (OAuth flow where page reloaded)
      const pending = sessionStorage.getItem("pendingJoinClubId");
      if (pending === clubId) {
        sessionStorage.removeItem("pendingJoinClubId");
        handleJoin();
      }
    }
  }, [user]);

  const handleJoin = async () => {
    if (!user) {
      pendingJoinRef.current = true;
      if (clubId) sessionStorage.setItem("pendingJoinClubId", clubId);
      openLoginModal();
      return;
    }

    if (!clubId) return;

    if (!isAcceptingMembers) {
      return;
    }

    if (isPrivate) {
      // Request-only club
      try {
        await requestToJoinClub({ clubId, userId: user.id }).unwrap();
        setRequestSuccess(true);
      } catch (error) {
        console.error("Failed to request to join:", error);
      }
      return;
    }

    if (isPaid) {
      // Paid club - redirect to pricing page
      navigate({
        to: "/clubs/$clubId/pricing",
        params: { clubId },
      });
      return;
    }

    // Free, open club - direct join
    try {
      await createClubMember({ clubId, userId: user.id }).unwrap();
      setJoinSuccess(true);
      // Invalidate relevant caches so the parent re-evaluates membership
      dispatch(
        clubsApi.util.invalidateTags([
          { type: "ClubMember", id: "LIST" },
          { type: "ClubMember", id: `LIST-${clubId}` },
          { type: "ClubMember", id: `${clubId}-${user.id}` },
          { type: "Club", id: clubId },
        ])
      );
    } catch (error) {
      console.error("Failed to join club:", error);
    }
  };

  // After successful join, don't show panel
  if (joinSuccess) {
    return null;
  }

  // Determine button content matching mobile join screen states
  const renderButtonContent = () => {
    if (isLoading) {
      return <Icon name="spinner" size={20} className="animate-spin" />;
    }

    if (!isAcceptingMembers) {
      return (
        <>
          <Icon name="hand-wave" size={20} />
          <span>I'd like to join</span>
        </>
      );
    }

    if (hasPendingRequest) {
      return (
        <>
          <Icon name="hourglass-half" size={20} />
          <span>Request pending</span>
        </>
      );
    }

    if (requestSuccess) {
      return (
        <>
          <Icon name="circle-check" size={20} />
          <span>Request sent!</span>
        </>
      );
    }

    if (isPrivate) {
      return <span>Request to join</span>;
    }

    return (
      <>
        <Icon name="hand-wave" size={20} />
        <span>Join this club</span>
      </>
    );
  };

  const buttonDisabled =
    isLoading || hasPendingRequest || requestSuccess || !isAcceptingMembers;

  return (
    <div className="px-4 mb-4">
      <div className="bg-surface-1 rounded-2xl p-6 max-w-[680px] mx-auto">
        <p className="text-body-1 text-text-secondary mb-4">
          Join this club to find out more about what's going on!
        </p>
        <Button
          onClick={handleJoin}
          disabled={buttonDisabled}
          variant="olive"
          className="w-full rounded-full text-[18px] font-medium py-[10px] px-6"
        >
          {renderButtonContent()}
        </Button>
      </div>
    </div>
  );
}
